import React, { useRef, useEffect, useState, useMemo } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Divider, Tooltip } from 'antd';
import { round2 } from "../../Utils/chart_utils";
import { getStatisticLatestDateString } from '../../Utils/chart_utils';

const COLOR_POSITIVE = "#3f8600";
const COLOR_NEGATIVE = "#cf1322";


const StatValue = ({ value, difference, is_low_good, fontSize, isAsym, isString, maxWidth, maxHeight, show_delta, numDecimals = null }) => {
  const containerRef = useRef(null);
  const valueRef = useRef(null);
  // The backend reports the decimal count the admin panel configured for the metric,
  // so a 2-decimal metric shows 0.50 and not 0.5. Without it the display is unchanged.
  const hasDecimals = Number.isInteger(numDecimals) && numDecimals >= 0;
  const shownValue = hasDecimals && typeof value === 'number' ? value.toFixed(numDecimals) : value;
  const shownDiff = hasDecimals ? Math.abs(difference).toFixed(numDecimals) : round2(Math.abs(difference));

  if (isString) {

    let fsize = 30;

    if (value.length > 40) {
      fsize = 15;
    }
    else if (value.length > 30) {
      fsize = 20;
    }
    else if (value.length > 20) {
      fsize = 25;
    }
    else if (value.length > 15) {
      fsize = 28;
    }
    else if (value.length > 10) {
      fsize = 30;
    }
    
    return (<div
      ref={containerRef}
      style={{
        width: "100%",
        height: '100%',
        overflow: 'scroll',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
      }}
    >
      <div style={{ textAlign: 'left', fontSize: fsize, lineHeight: 1.2, fontWeight: 600 }}>
        {value}
      </div>
    </div>
    )
    
  }

  return (
    <div
      ref={containerRef}
      className="StatValue"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        textAlign: "left",
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <div
        ref={valueRef}
        style={{
          fontSize: `${fontSize}px`,
          lineHeight: 1,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'left'
        }}
      >
        {shownValue}
      </div>
      <div
        style={{
          display: 'flex',
          color: difference * is_low_good >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE,
          fontSize: '13px',
          marginTop: '1px',
          justifyContent: 'start',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {show_delta ? (
          <>
          {difference >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          {shownDiff}
          </>
        ) : <>&nbsp;</>}
        
        
      </div>
    </div>
  );
};

const CompactStatisticNumberSuffix = ({ metricStat, isAsym, show_delta,dynamicFontSizing=false}) => {
  // const {
  //   latest,
  //   latestLeft,
  //   latestRight,
  //   diff,
  //   leftDiff,
  //   rightDiff,
  //   is_low_good
  // } = useMemo(() => ({
  //   latest: metricStat?.latest || metricStat?.latest_value || 0,
  //   latestLeft: isAsym ? metricStat?.left?.latest || metricStat?.left?.latest_value || 0 : 0,
  //   latestRight: isAsym ? metricStat?.right?.latest || metricStat?.right?.latest_value || 0 : 0,
  //   diff: metricStat?.diff || 0,
  //   leftDiff: isAsym ? metricStat?.left?.diff || 0 : null,
  //   rightDiff: isAsym ? metricStat?.right?.diff || 0 : null,
  //   is_low_good: metricStat?.low_good ? -1 : 1
  // }), [metricStat, isAsym]);

  const latest = metricStat?.latest || metricStat?.latest_value || 0;
  const latestLeft = isAsym ? metricStat?.left?.latest || metricStat?.left?.latest_value || 0 : 0;
  const latestRight = isAsym ? metricStat?.right?.latest || metricStat?.right?.latest_value || 0 : 0;
  const diff = metricStat?.diff || 0;
  const leftDiff = isAsym ? metricStat?.left?.diff || 0 : null;
  const rightDiff = isAsym ? metricStat?.right?.diff || 0 : null;
  const is_low_good = metricStat?.low_good ? -1 : 1;
  const latestDate = metricStat?.latest_date || null;
  const numDecimals = Number.isInteger(metricStat?.num_decimals) ? metricStat.num_decimals : null;


  if (metricStat === "-") return "-"
  if (metricStat?.latest_value === "No Data") {
    return (
      <div
        style={{
          fontSize: `30px`,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'left'
        }}
      >
        No Data
      </div>
    );
  };

  if (!metricStat || (!diff && !isAsym && latest === undefined)) {
    return null;
  }

  const metricIsString = (typeof latest) === 'string' ? true : false
  const textColor = metricStat?.team?.color || 'inherit';

  if (isAsym) {
    return (
      <div>
      <div style={{
        display: latestDate ? 'block' : 'none',
        whiteSpace: 'nowrap',
        fontSize: '10px',
        lineHeight: 1,
        padding: 0,
        justifySelf: 'left',
        margin: "0 0 3px 0px"
      }}>
        {getStatisticLatestDateString(metricStat)}
      </div>
      <div
        className="asym-CompactStatisticNumberSuffix"
        style={{
          display: 'flex',
          alignItems: 'start',
          color: textColor,
          overflow: 'hidden'
        }}
      >
        <StatValue
          value={latestLeft}
          difference={leftDiff}
          numDecimals={numDecimals}
          is_low_good={is_low_good}
          fontSize={dynamicFontSizing ? 30: 30}
          isAsym={true}
          maxWidth="calc(50% - 10px)"
          maxHeight={40}
          show_delta={show_delta}
        />
        <Divider
          type="vertical"
          style={{
            height: '50px',
            borderLeft: '2px solid rgba(0, 0, 0, 0.2)',
            flexShrink: 0
          }}
        />
        <StatValue
          value={latestRight}
          difference={rightDiff}
          numDecimals={numDecimals}
          is_low_good={is_low_good}
          fontSize={dynamicFontSizing ? 30: 30}
          isAsym={true}
          isString={metricIsString}
          maxWidth="calc(50% - 10px)"
          maxHeight={40}
          show_delta={show_delta}
        />
      </div>
      </div>

    );
  }
  
  return (
    <div
      style={{
        display: "flex",
        height: '100%',
        flexDirection: "row",
        alignItems: "center",
        color: textColor,
        width: '100%'
      }}
    >
      <div>
      <div style={{
        display: latestDate ? 'block' : 'none',
        whiteSpace: 'nowrap',
        fontSize: '10px',
        lineHeight: 1,
        padding: 0,
        justifySelf: 'left'
      }}>
        {getStatisticLatestDateString(metricStat)}
      </div>
      <StatValue value={latest} difference={diff} numDecimals={numDecimals} show_delta={show_delta} is_low_good={is_low_good} fontSize={metricIsString ? 16 : 35} isAsym={false} isString={metricIsString} maxWidth={100} maxHeight={50}/>
      </div>
    </div>
  );
};

const CompactStatisticNumber = ({ metricStat, isAsym, isCompact = false, show_delta=true ,dynamicFontSizing=false}) => {
  return (
    <div
      style={{
        flex: 1,
        flexDirection: "row",
        display: "flex",
        height: "100%",
        alignItems: "center",
        paddingTop: '0px',
      }}
    >
      <CompactStatisticNumberSuffix
        metricStat={metricStat}
        dynamicFontSizing={dynamicFontSizing}
        isAsym={metricStat.is_asym}
        isCompact={isCompact}
        show_delta={show_delta}
      />
    </div>
  );
};

export default React.memo(CompactStatisticNumber);